import { observable, action, reaction, makeObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import agent from '../agent';
import moment from 'moment';
import { payrollIntegrationOptions } from '../../utils/constants';

class CommonStore {
    constructor() {
        makeObservable(this, {
            locale: observable,
            connectionError: observable,
            config: observable,
            token: observable,
            appLoaded: observable,
            currentGitRev: observable,
            wageTypeMap: observable,
            costUnitsAndTypes: observable,
            integrationMetaData: observable,
            isUserLoggedIn: observable,
            holidayDataMap: observable,
            companyShiftMap: observable,
            absenceTypeLabelMap: observable,
            weeklyOvertimeSettings: observable,
            weeklyOvertimeCapSettings: observable,
            pageSizeByList: observable,
            getGitRev: action,
            setGitRev: action,
            setToken: action,
            setAppLang: action,
            setAppLoaded: action,
            getConfig: action,
            log: action,
            addNotification: action,
            setLocale: action,
            getUsername: action,
            sendContact: action,
            getCostUnitsAndTypes: action,
            refetchWagetypes: action,
            setHolidayDateSet: action,
            getHolidayDetails: action,
            loadUserOvertimeSettingOptions: action,
            setPageSizeForList: action
        });

        reaction(
            () => this.token,
            (token) => {
                if (token) {
                    window.sessionStorage.setItem('jwt', token);
                } else {
                    window.sessionStorage.removeItem('jwt');
                    window.sessionStorage.removeItem('multiAdminJwt');
                }
            }
        );
    }

    locale = window.sessionStorage.getItem('locale') || 'no';
    connectionError = false;
    config = {};
    token = window.sessionStorage.getItem('jwt');
    appLoaded = false;
    currentGitRev = null;
    wageTypeMap = null;
    costUnitsAndTypes = null;
    integrationMetaData = null;
    isUserLoggedIn = false;
    holidayDataMap = new Map();
    companyShiftMap = null;
    absenceTypeLabelMap = null;
    weeklyOvertimeSettings = [];
    weeklyOvertimeCapSettings = [];
    pageSizeByList = {
        project_members: 10,
        task_members: 10,
        user_project_assign: 10,
        bulk_user_profile_update: 10,
        bulk_project_assign: 10,
    };

    async getGitRev() {
        try {
            const rev = await agent.Users.gitRev();
            return rev.rev;
        } catch (e) {
            return 'local';
        }
    }

    setGitRev(rev) {
        this.currentGitRev = rev;
    }

    setToken(token) {
        this.token = token;
        if (token) this.setAppLoaded();
    }

    setAppLang(lang) {
        this.locale = lang;
    }

    setIsUserLoggedIn(value) {
        this.isUserLoggedIn = value;
    }

    setPageSizeForList(listId, size) {
        this.pageSizeByList = { ...this.pageSizeByList, [listId]: size };
    }

    setAppLoaded(isReload = true) {
        if (!this.token && isReload) {
            this.appLoaded = true;
            return null;
        }
        return agent.Auth.config()
            .then(
                action((config) => {
                    this.config = config;
                    this.connectionError = false;
                    this.appLoaded = true;
                    this.setWageTypes(config?.integration_details);
                    this.setHolidayDateSet();
                    this.getCostUnitsAndTypes();
                    this.getIntegrationMetaData();
                    this.setCompanyShiftCode(config.companyShifts);
                    this.setAbsenceTypeLabels(config?.client?.data?.extendedRules?.absent_types);
                    return config;
                })
            )
            .catch((e) => {
                this.connectionError = true;
                this.appLoaded = true;
            });
    }

    setCompanyShiftCode(companyShifts) {
        const companyShiftMap = new Map();
        companyShifts.forEach(ele => {
            if (ele?.id) {
                companyShiftMap.set(ele.id, ele);
            }
        });
        this.companyShiftMap = companyShiftMap;
    }

    getCompanyShift(id) {
        return this.companyShiftMap?.get(id);
    }

    setAbsenceTypeLabels(absentTypes) {
        const absenceTypeLabelMap = new Map();
        (absentTypes || []).forEach(ele => {
            if (ele?.code) {
                absenceTypeLabelMap.set(ele.code, ele.name);
            }
        });
        this.absenceTypeLabelMap = absenceTypeLabelMap;
    }
    
    getAbsenceTypeLabel(code) {
        return this.absenceTypeLabelMap?.get(code) || code;
    }
    
    sortCompanyShifts(companyShifts = [], ignoreDefault = false, fCodeFlags = null, allowStandbyShifts = false) {
        if (!Array.isArray(companyShifts) || companyShifts.length === 0) return [];
        let shifts = companyShifts;
        if (fCodeFlags !== null || allowStandbyShifts) {
            const CONDITIONAL_FCODES = ['F1', 'F2', 'F3', 'F4', 'F5'];
            shifts = companyShifts.filter(shift => {
                if (!allowStandbyShifts && shift.shift_type === 'standbyShift') return false;
                if (fCodeFlags !== null && CONDITIONAL_FCODES.includes(shift.short_code)) return !!fCodeFlags[shift.short_code];
                return true;
            });
        }
        const shiftsToSort = ignoreDefault ? shifts.slice(1) : [...shifts];
        const sorted = shiftsToSort.sort((a, b) => {
            const startDiff = a.start_time - b.start_time;
            return startDiff !== 0 ? startDiff : a.end_time - b.end_time;
        });
        return ignoreDefault ? [shifts[0], ...sorted] : sorted;
    }

    setWageTypes(integration_details) {
        const wageType = new Map();
        const { data, last_updated, leaveActivity = [], absenceCode = [] } = integration_details?.wageType || {};
        if (integration_details.product === payrollIntegrationOptions.VISMA) {
            data.forEach((ele) => {
                const value = ele?.items.map((item) => ({
                    value: item?.shortCode,
                    label: `${item?.displayName}`,
                }));
                wageType.set(ele?.shortCode, value);
            });
        } else if (integration_details.product === payrollIntegrationOptions.TRIPLETEX) {
            data.forEach((ele) => {
                const value = ele?.items.map((item) => ({
                    value: `${item?.id}`,
                    label: `${item?.name}`,
                }));
                wageType.set(ele?.shortCode, value);
            });
        } else if (integration_details.product === payrollIntegrationOptions.XLEDGER) {
            const groupedTypes = {
                'ab': leaveActivity,
                'at': data.filter(type => type.overtime),
                'su': data.filter(type => type.timeTypeCategory?.name === 'WORKING_HOURS'),
                'other': data.filter(type => type.timeTypeCategory?.name === 'OTHER'),
                // 'absenceCode': absenceCode
            };

            // Set each category in the map
            Object.entries(groupedTypes).forEach(([key, types]) => {
                wageType.set(key, types.map(type => {
                    const result = {
                        value: (type.dbId).toString(),
                        label: key === 'ab' ? type.code + '-' + type.description + '|' + type.projectCode : type.description
                    }
                    if (key === 'ab') result.projectDbId = type.projectDbId
                    return result;
                }));
            });
        }
        wageType.set('last_updated', last_updated);
        this.wageTypeMap = wageType;
    }

    getWageTypes(wageTypeCode) {
        return this.wageTypeMap?.get(wageTypeCode);
    }

    getWageTypesAccordingToIntegrations(integration_details, wageTypeCode) {
        if (integration_details.product === payrollIntegrationOptions.TRIPLETEX) {
            if (wageTypeCode === 'last_updated') return this.getWageTypes(wageTypeCode);
            return this.getWageTypes(wageTypeCode === 'ab' ? 'ab' : 'su');
        } else if (integration_details.product === payrollIntegrationOptions.XLEDGER) {
            return this.getWageTypes(wageTypeCode !== 'su' ? wageTypeCode : 'su');
        } else return this.getWageTypes(wageTypeCode);
    }

    // Union of every wage-type category returned by the integration, deduped by value.
    // Fallback for when the category we guessed (ab/at/su) doesn't hold the wage type the admin needs.
    getAllWageTypesCombined() {
        if (!this.wageTypeMap) return [];
        const seen = new Set();
        const combined = [];
        this.wageTypeMap.forEach((list, key) => {
            if (key === 'last_updated' || !Array.isArray(list)) return;
            list.forEach(item => {
                if (item?.value != null && !seen.has(item.value)) {
                    seen.add(item.value);
                    combined.push(item);
                }
            });
        });
        return combined;
    }

    getConfig() {
        return this.config;
    }

    log(log) {
        agent.Auth.log(log);
    }

    setHolidayDateSet() {
        const holidays = this.config?.clientHolidays?.flatMap((holidayYear) => holidayYear.data?.holidays || []) || [];

        this.holidayDataMap = new Map();

        holidays.forEach((holiday) => {
            const formattedDate = moment(holiday.date).format('YYYY-MM-DD');
            this.holidayDataMap.set(formattedDate, {
                name: holiday.name,
                type: Array.isArray(holiday.type) ? holiday.type.join(', ') : holiday.type || '',
                custom_name: holiday.custom_name,
                date: holiday.date,
                accountedInAbsence: holiday.accountedInAbsence,
                accountedInVacation: holiday.accountedInVacation,
                additionalPayment: holiday.additionalPayment,
            });
        });
    }

    isHoliday(date) {
        return this.holidayDataMap.has(moment(date).format('YYYY-MM-DD'));
    }

    getHolidayDetails(date) {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        return this.holidayDataMap.get(formattedDate) || null;
    }

    getHolidayInfo(date) {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        const holidayDetails = this.holidayDataMap.get(formattedDate);
        return {
            isHoliday: !!holidayDetails,
            details: holidayDetails || null
        };
    }

    /**
     * Create a notification displayed with `react-notification-system`
     *
     * @param message Message of the notification.
     * @param [title=null] Title of the notification.
     * @param [level="info"] Level of the notification. Available: success, error, warning and info.
     * @param [position="tc"] Position of the notification. Available: tr (top right), tl (top left), tc (top center), br (bottom right), bl (bottom left), bc (bottom center).
     * @param [autoDismiss=5] Delay in seconds for the notification go away. Set this to 0 to not auto-dismiss the notification.
     */
    addNotification(message, title = null, level = 'info', position = 'top-right', autoDismiss = 1000) {
        const uid = new Date().getTime().toString();
        return toast(message, {
            toastId: uid,
            type: level,
            position: position,
            autoClose: autoDismiss,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
        });
    }

    dismissToast(uid) {
        toast.dismiss(uid);
    }

    setLocale(lang) {
        agent.Auth.setLang(lang);
    }

    getUsername(id) {
        if (!this.config || !this.config.pms) return '';
        return this.config.pms[id];
    }

    sendContact(params) {
        return agent.Dashboard.contact(params);
    }

    async getCostUnitsAndTypes(params = {}) {
        try {
            if (this?.config?.integration_details?.product === payrollIntegrationOptions.VISMA) {
                this.costUnitsAndTypes = {};
                const data = await agent.Users.getCostUnitsAndTypes(params);
                runInAction(() => {
                    this.costUnitsAndTypes = data;
                });
            }
            return this.costUnitsAndTypes;
        } catch (err) {
            throw err;
        }
    }

    async getIntegrationMetaData(params = {}) {
        try {
            if (this.config?.integration_details?.product === payrollIntegrationOptions.XLEDGER) {
                const metaData = await agent.Users.getIntegrationMetaData(params);
                runInAction(() => {
                    this.integrationMetaData = metaData;
                });
                return metaData;
            }
        } catch (err) {
            throw err;
        }
    }

    async refetchWagetypes() {
        try {
            if (this?.config?.integration_details?.product) {
                this.config.integration_details = await agent.Users.refetchWagetypes();
                runInAction(() => {
                    this.setWageTypes(this.config.integration_details);
                });
            }
        } catch (err) {
            throw err;
        }
    }

    getDateTimeRules() {
        const dateTimeRules = this?.config?.client?.data?.dateTimeRules || false;
        return dateTimeRules;
    }

    checkIfClientHasUrgentCallSettings(type = null, isFromPayroll = false, returnExtraPayments = false) {
        const clientData = this?.config?.client?.data;
        if (returnExtraPayments)
            return clientData.extraPayments.find(ele => ele.isActive && ele.code === type);

        let hasUrgentCallSettings = null;
        if (clientData && clientData.extraPayments) {
            if (!type) {
                const urgentCallRules = clientData.extraPayments.filter(ele => ele.code && ele.code.split && ele.code.split('_').includes('urgent'));
                if (!urgentCallRules.length) {
                    hasUrgentCallSettings = false
                } else {
                    hasUrgentCallSettings = urgentCallRules.some(e => e.isActive);
                }
            }
            else if (isFromPayroll)
                hasUrgentCallSettings = clientData.extraPayments.some(ele => ele.code === type && ele.isActive && ele.payrollWageType && ele.payrollWageType.value);
            else {
                hasUrgentCallSettings = clientData.extraPayments.some(ele => ele.code === type && ele.isActive);
            }
        }
        return hasUrgentCallSettings;
    }

    loadUserOvertimeSettingOptions() {
        return Promise.all([
            agent.Clients.getWeeklyOvertimeSettings(),
            agent.Clients.getOvertimeCapSettings()
        ])
            .then(
                action(([weeklyOvertimeSettings, overtimeCapSettings]) => {
                    this.weeklyOvertimeSettings = weeklyOvertimeSettings.weeklyOvertimeSettings || [];
                    this.weeklyOvertimeCapSettings = overtimeCapSettings.overtimeCapSettings || [];
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }
}

const _CommonStore = new CommonStore();
export default _CommonStore;
